import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { map, of, switchMap } from 'rxjs';
import { WorkshopSet } from '../entities/WorkshopSet.js';
import { BHFilter } from '../entities/bh-filter.js';
import { ProductFilter } from '../entities/products.js';
import { Workshop, WorkshopAffiliation } from '../entities/workshop.js';

export const subscribeWorkshopSetPaginated = registerSubscription({
  operationName: 'subscribeWorkshopSetPaginated',
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  type: () => [WorkshopSet],
  resolve: (skip: number, limit: number, filter: BHFilter) => (gqlFields: BaseQLSelectionSet<WorkshopSet>) => {
    if (!filter.institutionID) return of([]);

    const aggregation = [
      { $match: filter.getMongoFilter() },
      { $sort: filter.getMongoOptions().sort },
      { $skip: skip },
      { $limit: limit },
      { $project: gqlFields },
    ];

    const getCollection = getRawCollection<WorkshopSet>(WorkshopSet._collectionName);
    return watchEntireCollection(WorkshopSet._collectionName).pipe(
      switchMap(() => getCollection),
      switchMap(c => c.aggregate(aggregation, { collation: { locale: 'de' } }).toArray()),
      map(result => result as WorkshopSet[]),
    );
  },
});

export const saveWorkshopSet = registerMutation({
  operationName: 'saveWorkshopSet',
  params: [
    { name: 'workshopSet', type: () => WorkshopSet },
    { name: 'workshopIDs', type: () => [String] },
  ],
  type: () => WorkshopSet,
  resolve: (workshopSet: WorkshopSet, workshopIDs: string[]) => async (gqlFields: BaseQLSelectionSet<WorkshopSet>) => {
    if (workshopSet._id) await workshopSet.save();
    else {
      const workshopSetID = await workshopSet.create();
      workshopSet._id = workshopSetID;
    }

    // remove affiliation from workshops not in the set
    const workshopCollection = await getRawCollection<Workshop>(Workshop._collectionName);
    await workshopCollection.updateMany(
      {
        _id: { $nin: workshopIDs },
        'affiliation.affiliation': workshopSet._class,
        'affiliation.affiliationID': workshopSet._id,
      },
      { $set: { affiliation: null } },
    );

    // add affiliation to workshops in the set
    for (const workshopID of workshopIDs) {
      const workshop = await getByID(Workshop, workshopID)({ _id: 1, affiliation: WorkshopAffiliation.allFields });

      if (workshop === null) throw new Error('Workshops must already exist to be part of a workshop-set');
      workshop.affiliation = new WorkshopAffiliation({
        affiliation: workshopSet._class,
        affiliationID: workshopSet._id,
      });

      await workshop.save();
    }

    return workshopSet;
  },
});

export const subscribeAllActiveWorkshopSets = registerSubscription({
  operationName: 'subscribeAllActiveWorkshopSets',
  type: () => [WorkshopSet],
  params: [{ name: 'filter', type: () => ProductFilter }],
  resolve: (filter: Maybe<ProductFilter>) => {
    return <SelSet extends BaseQLSelectionSet<WorkshopSet>>(gqlFields: SelSet) => {
      if (!filter) return of([]);
      const institutionFilter = filter.institutionIDs ? { institutionID: { $in: filter.institutionIDs } } : {};
      const timeframeFilter = filter.timeframeIDs ? { timeframeID: { $in: filter.timeframeIDs } } : {};
      const categoryFilter = filter.categoryIDs ? { categoryID: { $in: filter.categoryIDs } } : {};

      const aggregation = [
        { $match: { $and: [institutionFilter, timeframeFilter, categoryFilter] } },
        // TODO: lookup workshops
        // { $match: filter.getMongoFilter() ?? {} },
        // { $sort: filter?.getMongoOptions().sort ?? {} },
      ];

      return watchEntireCollection(WorkshopSet._collectionName).pipe(
        switchMap(async () => getRawCollection<WorkshopSet>(WorkshopSet._collectionName)),
        switchMap(c => c.aggregate(aggregation, { collation: { locale: 'de' } }).toArray()),
        map(result => result as WorkshopSet[]),
      );
    };
  },
});
